<script lang="ts">
  import ActionsMenu, { type ActionItem } from './ActionsMenu.svelte';
  import Icon from './Icon.svelte';
  import { photoUrl } from './api';
  import { relativeTime } from './time';
  import type { DashboardTripView } from '~/shared/api.js';

  let {
    trip,
    onRename,
    onShare,
    onDelete,
  }: {
    trip: DashboardTripView;
    onRename?: (trip: DashboardTripView) => void;
    onShare?: (trip: DashboardTripView) => void;
    onDelete?: (trip: DashboardTripView) => void;
  } = $props();

  const cover = $derived(photoUrl(trip.coverPhotoReference, 720));

  const menuItems: ActionItem[] = $derived.by(() => {
    const items: ActionItem[] = [];
    if (onShare) items.push({ kind: 'button', label: 'Share', onClick: () => onShare(trip) });
    if (onRename) items.push({ kind: 'button', label: 'Rename', onClick: () => onRename(trip) });
    if (onDelete) {
      items.push({
        kind: 'button',
        label: 'Delete trip',
        destructive: true,
        onClick: () => onDelete(trip),
      });
    }
    return items;
  });
</script>

<div class="card-root">
  <a class="trip-card" href={`/t/${trip.token}`}>
    <div class="cover">
      {#if cover}
        <img src={cover} alt="" loading="lazy" />
      {:else}
        <div class="cover-placeholder">
          <Icon name="sparkles" size={28} />
        </div>
      {/if}
    </div>
    <div class="body">
      <h3 class="name">{trip.name}</h3>
      <p class="meta">
        {#if trip.primaryCityName}
          <span class="city">{trip.primaryCityName}</span>
          <span class="dot" aria-hidden="true">·</span>
        {/if}
        <span class="count">
          {trip.poiCount} {trip.poiCount === 1 ? 'place' : 'places'}
        </span>
      </p>
      <p class="timestamp">Updated {relativeTime(trip.updatedAt)}</p>
    </div>
  </a>
  {#if menuItems.length > 0}
    <div class="card-menu">
      <ActionsMenu items={menuItems} label="Trip actions" size="sm" emphasis="filled" />
    </div>
  {/if}
</div>

<style>
  .card-root {
    position: relative;
  }
  .trip-card {
    display: flex;
    flex-direction: column;
    text-decoration: none;
    color: inherit;
    background: var(--color-surface);
    border: 1px solid var(--color-hairline-soft);
    border-radius: var(--radius-md);
    overflow: hidden;
    transition: transform 150ms ease, box-shadow 150ms ease, border-color 150ms ease;
  }
  .trip-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px -12px rgba(0, 0, 0, 0.18);
    border-color: var(--color-hairline);
  }
  .card-menu {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    z-index: 1;
  }
  .cover {
    aspect-ratio: 16 / 10;
    background: var(--color-surface-strong, var(--color-surface));
    overflow: hidden;
  }
  .cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  .cover-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-muted);
    background:
      linear-gradient(135deg, var(--color-surface-soft, var(--color-surface)) 0%,
                              var(--color-surface-strong, var(--color-surface)) 100%);
  }
  .body {
    padding: 0.875rem 1rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    min-width: 0;
  }
  .name {
    margin: 0;
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .meta {
    margin: 0;
    font-size: 13px;
    color: var(--color-body);
    display: flex;
    align-items: baseline;
    gap: 0.375rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .city {
    font-weight: 500;
    color: var(--color-ink);
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .dot {
    color: var(--color-muted);
  }
  .timestamp {
    margin: 0;
    font-size: 12px;
    color: var(--color-muted);
  }
</style>
